import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useSession as useBetterAuthSession } from '@/lib/auth';
import { API_URL, fetchWithTimeout } from '@/lib/api/core';
import { isEmbeddedContext } from '@/lib/embedded-auth';

type SessionResult = ReturnType<typeof useBetterAuthSession>;

interface AuthStateContextValue {
  sessionResult: SessionResult;
  isAuthenticated: boolean;
  isReady: boolean;
}

const AuthStateContext = createContext<AuthStateContextValue | null>(null);

// Web: the normal Better Auth session hook.
function WebAuthProvider({ children }: { children: ReactNode }) {
  const sessionResult = useBetterAuthSession();
  const isAuthenticated = !sessionResult.isPending && !!sessionResult.data;
  return (
    <AuthStateContext.Provider
      value={{ sessionResult, isAuthenticated, isReady: !sessionResult.isPending }}
    >
      {children}
    </AuthStateContext.Provider>
  );
}

// Embedded side-panel iframe (chrome-extension:// partition): Better Auth's
// useSession NEVER settles here — its internal cookie/localStorage handling
// can't complete in a third-party storage partition, and its constant churn
// drives endless re-renders that keep the UI on loading skeletons. So we do NOT
// call it at all. Instead resolve the session with a plain get-session query
// (fetchWithTimeout sends the Bearer token when present, and the partitioned
// cookie otherwise) — it always SETTLES, to authenticated or signed-out, where
// the Better Auth hook would just hang.
function EmbeddedAuthProvider({ children }: { children: ReactNode }) {
  const embedSession = useQuery<{ user?: unknown; session?: unknown } | null>({
    queryKey: ['embedded-auth-session'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetchWithTimeout(`${API_URL}/api/auth/get-session`);
      if (!res.ok) return null;
      return (await res.json()) as { user?: unknown; session?: unknown } | null;
    },
  });

  const sessionResult: SessionResult = {
    data: embedSession.data ?? null,
    isPending: embedSession.isPending,
    error: embedSession.error ?? null,
    refetch: embedSession.refetch,
  } as unknown as SessionResult;

  const isAuthenticated = !sessionResult.isPending && !!sessionResult.data;

  return (
    <AuthStateContext.Provider
      value={{ sessionResult, isAuthenticated, isReady: !sessionResult.isPending }}
    >
      {children}
    </AuthStateContext.Provider>
  );
}

export function AuthStateProvider({ children }: { children: ReactNode }) {
  // Branch on the iframe CONTEXT, not on the token. A token-less embed (expired
  // extension creds → bootstrap couldn't mint one) must still take the embedded
  // path — falling back to WebAuthProvider there mounts Better Auth's useSession,
  // which never settles in the partition and hangs the panel on a spinner. The
  // context is stable for the SPA lifetime, so the branch never switches (no
  // hook-order violation), and the embedded path never mounts the hook.
  const isEmbedded = isEmbeddedContext();
  return isEmbedded ? (
    <EmbeddedAuthProvider>{children}</EmbeddedAuthProvider>
  ) : (
    <WebAuthProvider>{children}</WebAuthProvider>
  );
}

export function useAuthState() {
  const context = useContext(AuthStateContext);
  if (!context) {
    throw new Error('useAuthState must be used within AuthStateProvider');
  }

  return {
    session: context.sessionResult.data ?? null,
    sessionResult: context.sessionResult,
    isAuthenticated: context.isAuthenticated,
    isReady: context.isReady,
  };
}

